import { cloneDeep, isEqual } from "lodash";
import ILessonPageDto from "../models/ILessonPageDto";
import { MirrorableObjects, mirrorObjectsOntoPageManifest } from "../utils/lessonPages/mirrorObjectsOntoPageManifest";

interface SyncDisplayedPageStateParams {
  lessonPages: ILessonPageDto[];
  currentlyDisplayedPageIndex: number;
  objects: MirrorableObjects;
  /**
   * objectsState.objectListHistory.length > 0 — the signal saveLesson already
   * uses to decide whether the user actually edited the displayed page.
   */
  hasUnsavedObjectEdits: boolean;
}

/**
 * Flushes the displayed page's in-progress object edits into the builder's own
 * `lessonPages` state.
 *
 * goToPage commits those edits to the LessonPagesProvider context only, so
 * without this the builder's `lessonPages` still holds the page as it was before
 * the edits. A later page operation (deletePage, addNewPageAndDisplay, saveLesson)
 * rebuilds from builder state and silently drops them (ticket 25649).
 *
 * The gates matter as much as the mirror. ObjectsProvider starts at an empty
 * initialState and nothing populates it when a lesson is opened, so mirroring
 * unconditionally would blank the first page the moment the user clicks away from
 * it. `hasUnsavedObjectEdits` is what proves objectsState actually represents the
 * displayed page, and the Base/freeForm checks keep this in step with saveLesson,
 * which never merges objectsState into any other page type.
 *
 * The manifest is deep-cloned before mutation because manifests can originate
 * from the immer-produced provider state and be frozen.
 *
 * Returns the original array unchanged when there is nothing to flush, so the
 * caller's setState can bail out instead of forcing a re-render.
 */
export function syncDisplayedPageState({
  lessonPages,
  currentlyDisplayedPageIndex,
  objects,
  hasUnsavedObjectEdits,
}: SyncDisplayedPageStateParams): ILessonPageDto[] {
  if (!hasUnsavedObjectEdits) {
    return lessonPages;
  }

  const currentPage = lessonPages?.[currentlyDisplayedPageIndex];
  if (!currentPage || currentPage.pagePlayerType !== "Base" || !currentPage.pageManifest) {
    return lessonPages;
  }

  const clonedPageManifest = cloneDeep(currentPage.pageManifest);
  mirrorObjectsOntoPageManifest(clonedPageManifest, objects, clonedPageManifest.basePageType === "freeForm");

  if (isEqual(clonedPageManifest, currentPage.pageManifest)) {
    return lessonPages;
  }

  const updatedPages = [...lessonPages];
  updatedPages[currentlyDisplayedPageIndex] = {
    ...currentPage,
    pageManifest: clonedPageManifest,
    pageManifestIsDirty: true,
  };

  return updatedPages;
}
